/* Truth Scriptures: global interactions (Ahmed Development)
   Scroll reveal, header, dropdowns, drawers, accordions, carousels,
   sticky bars, announcement rotation and testimonial modals.
   No libraries; one shared IntersectionObserver for reveals. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  /* ---------- Scroll reveal ---------- */
  let revealObserver;
  const initReveal = (root = document) => {
    const items = root.querySelectorAll('.ts-reveal:not(.is-revealed)');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || reduceMotion.matches || window.Shopify?.designMode) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    revealObserver =
      revealObserver ||
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    items.forEach((el) => revealObserver.observe(el));
  };
  window.tsInitReveal = initReveal;

  /* ---------- Input modality: the focus ring is for keyboard users only ---------- */
  const rootEl = document.documentElement;
  document.addEventListener('pointerdown', () => rootEl.classList.add('ts-pointer'), { capture: true, passive: true });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Tab' || e.key.startsWith('Arrow')) rootEl.classList.remove('ts-pointer');
  }, true);

  /* ---------- Scroll lock + focus trap helpers ---------- */
  let lockCount = 0;
  const lockScroll = () => {
    lockCount += 1;
    document.documentElement.classList.add('ts-scroll-lock');
  };
  const unlockScroll = () => {
    lockCount = Math.max(0, lockCount - 1);
    if (!lockCount) document.documentElement.classList.remove('ts-scroll-lock');
  };
  const trapFocus = (container, event) => {
    if (event.key !== 'Tab') return;
    const nodes = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---------- Drawer (mobile menu, filters, cart base) ---------- */
  class TsDrawer extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.panel = this.querySelector('.ts-drawer__panel');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-ts-close]')) {
          e.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        else if (this.isOpen) trapFocus(this.panel || this, e);
      });
    }
    get isOpen() {
      return this.classList.contains('is-open');
    }
    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      this.removeAttribute('inert');
      lockScroll();
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
      const focusTarget = this.querySelector('[data-ts-autofocus]') || this.panel;
      const focusIn = () => { if (this.isOpen && focusTarget && !this.contains(document.activeElement)) focusTarget.focus({ preventScroll: true }); };
      window.setTimeout(focusIn, reduceMotion.matches ? 0 : 60);
      window.setTimeout(focusIn, 400); // again after the slide-in, in case a third-party frame took focus
      // Modal: while open, focus can't wander outside the drawer (it's returned to the panel).
      this._guard = (e) => { if (this.isOpen && !this.contains(e.target) && !e.target.closest('dialog[open]')) focusTarget.focus({ preventScroll: true }); };
      document.addEventListener('focusin', this._guard);
      this.dispatchEvent(new CustomEvent('ts:drawer-open', { bubbles: true }));
    }
    close() {
      if (!this.isOpen) return;
      if (this._guard) document.removeEventListener('focusin', this._guard);
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('inert', '');
      unlockScroll();
      if (this.trigger && this.trigger.setAttribute) {
        this.trigger.setAttribute('aria-expanded', 'false');
        if (document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
      }
    }
  }
  window.TsDrawer = TsDrawer;
  if (!customElements.get('ts-drawer')) customElements.define('ts-drawer', TsDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-ts-open]');
    if (!opener) return;
    const target = document.getElementById(opener.getAttribute('data-ts-open'));
    if (target && typeof target.open === 'function') {
      e.preventDefault();
      target.open(opener);
    }
  });

  /* ---------- Header: shrink on scroll + desktop dropdowns ---------- */
  const initHeader = () => {
    const header = document.querySelector('[data-ts-header]');
    if (!header) return;
    let ticking = false;
    const update = () => {
      const scrolled = window.scrollY > 24;
      header.classList.toggle('is-scrolled', scrolled);
      // page-level flag so --header-h (sticky offsets, anchors) follows the header height
      document.documentElement.classList.toggle('ts-header-scrolled', scrolled);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();

    header.querySelectorAll('[data-ts-dropdown]').forEach((item) => {
      const button = item.querySelector('button');
      let closeTimer;
      const set = (open) => {
        item.classList.toggle('is-open', open);
        button.setAttribute('aria-expanded', String(open));
      };
      button.addEventListener('click', () => set(!item.classList.contains('is-open')));
      item.addEventListener('mouseenter', () => {
        if (!window.matchMedia('(hover: hover)').matches) return;
        clearTimeout(closeTimer);
        set(true);
      });
      item.addEventListener('mouseleave', () => {
        if (!window.matchMedia('(hover: hover)').matches) return;
        closeTimer = setTimeout(() => set(false), 120);
      });
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) set(false);
      });
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && item.classList.contains('is-open')) {
          set(false);
          button.focus();
        }
      });
    });
    document.addEventListener('click', (e) => {
      header.querySelectorAll('[data-ts-dropdown].is-open').forEach((item) => {
        if (!item.contains(e.target)) {
          item.classList.remove('is-open');
          item.querySelector('button').setAttribute('aria-expanded', 'false');
        }
      });
    });
  };

  /* ---------- Accordions: animated details/summary ---------- */
  const initAccordions = (root = document) => {
    root.querySelectorAll('details[data-ts-acc]:not([data-ts-ready])').forEach((details) => {
      details.setAttribute('data-ts-ready', '');
      const summary = details.querySelector('summary');
      const content = details.querySelector('.ts-acc__content');
      if (!summary || !content) return;
      summary.setAttribute('aria-expanded', String(details.open));
      summary.addEventListener('click', (e) => {
        if (reduceMotion.matches) {
          window.requestAnimationFrame(() => summary.setAttribute('aria-expanded', String(details.open)));
          return;
        }
        e.preventDefault();
        if (details.dataset.animating) return;
        details.dataset.animating = '1';
        const opening = !details.open;
        if (opening) details.open = true;
        const start = opening ? 0 : content.scrollHeight;
        const end = opening ? content.scrollHeight : 0;
        content.style.height = `${start}px`;
        const anim = content.animate([{ height: `${start}px` }, { height: `${end}px` }], {
          duration: 300,
          easing: 'cubic-bezier(.2,.7,.2,1)',
        });
        summary.setAttribute('aria-expanded', String(opening));
        anim.onfinish = () => {
          content.style.height = '';
          if (!opening) details.open = false;
          delete details.dataset.animating;
        };
      });
    });
  };
  window.tsInitAccordions = initAccordions;

  /* ---------- Dot row: ONE button for the whole row ----------
     The 6px dots sit 8px apart, so separate 24px (32px on touch) targets
     would overlap. The row is a single target instead: a tap goes to the
     nearest dot; Enter/Space (keyboard) goes to the next slide. */
  const makeDots = (container, count, onPick, label) => {
    container.innerHTML = '';
    if (count < 2) return null;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ts-dots__btn';
    btn.innerHTML = Array.from({ length: count }, () => '<span class="ts-dot" aria-hidden="true"></span>').join('');
    let current = 0;
    btn.addEventListener('click', (e) => {
      if (e.detail === 0) return onPick((current + 1) % count);
      let best = 0;
      let dist = Infinity;
      [...btn.children].forEach((d, n) => {
        const r = d.getBoundingClientRect();
        const dd = Math.abs((r.left + r.right) / 2 - e.clientX);
        if (dd < dist) { dist = dd; best = n; }
      });
      onPick(best);
    });
    container.appendChild(btn);
    const next = (window.tsTheme && window.tsTheme.strings && window.tsTheme.strings.nextSlide) || 'Next';
    const setActive = (i) => {
      current = i;
      [...btn.children].forEach((d, n) => d.classList.toggle('is-active', n === i));
      btn.setAttribute('aria-label', `${label || 'Slide'} ${i + 1} / ${count}. ${next}`);
    };
    setActive(0);
    return { btn, setActive };
  };
  window.tsMakeDots = makeDots;

  /* ---------- Shared horizontal scroller (.ts-scroller) ----------
     No native scrollbar; round arrows at both ends appear only when the
     row overflows and there is more to scroll in that direction. */
  const initScrollers = (root = document) => {
    root.querySelectorAll('[data-ts-scroller]:not([data-ts-ready])').forEach((el) => {
      el.setAttribute('data-ts-ready', '');
      const track = el.querySelector('[data-ts-scroller-track]');
      const prev = el.querySelector('[data-ts-scroller-prev]');
      const next = el.querySelector('[data-ts-scroller-next]');
      if (!track) return;
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const overflow = max > 1;
        el.classList.toggle('is-overflowing', overflow);
        if (prev) prev.hidden = !overflow || track.scrollLeft <= 1;
        if (next) next.hidden = !overflow || track.scrollLeft >= max - 1;
      };
      const by = (dir) => track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      prev?.addEventListener('click', () => by(-1));
      next?.addEventListener('click', () => by(1));
      track.addEventListener('scroll', update, { passive: true });
      new ResizeObserver(update).observe(track);
      update();
    });
  };
  window.tsInitScrollers = initScrollers;

  /* ---------- Carousel: scroll-snap + arrows + dots ---------- */
  class TsCarousel extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.track = this.querySelector('[data-ts-track]');
      this.prev = this.querySelector('[data-ts-prev]');
      this.next = this.querySelector('[data-ts-next]');
      this.dots = this.querySelector('[data-ts-dots]');
      if (!this.track) return;
      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.go(1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.go(-1);
        }
      });
      this.ro = new ResizeObserver(() => this.build());
      this.ro.observe(this.track);
    }
    disconnectedCallback() {
      this.ro?.disconnect();
    }
    get slides() {
      return [...this.track.children];
    }
    get step() {
      const s = this.slides;
      if (s.length < 2) return this.track.clientWidth;
      return s[1].offsetLeft - s[0].offsetLeft;
    }
    get pages() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const step = this.step;
      if (max <= 2 || !(step > 0)) return 1;
      return Math.round(max / step) + 1;
    }
    build() {
      // Not laid out yet (hidden or zero-width slides): wait for the next resize.
      if (!this.track.clientWidth || !(this.step > 0)) return;
      const pages = this.pages;
      this.classList.toggle('is-static', pages <= 1);
      const useCounter = pages > 7;
      this.classList.toggle('has-counter', useCounter);
      if (useCounter && this.dots) {
        this.dots.innerHTML = '';
        if (!this.counter) {
          this.counter = document.createElement('p');
          this.counter.className = 'ts-counter';
          this.counter.setAttribute('aria-live', 'polite');
          this.dots.after(this.counter);
        }
        this.ensureArrows();
      } else {
        if (this.counter) { this.counter.remove(); this.counter = null; }
        this.querySelectorAll('.ts-carousel__nav [data-ts-auto]').forEach((b) => b.remove());
        if (this._ownPrev !== undefined) { this.prev = this._ownPrev; this.next = this._ownNext; }
      }
      if (this.dots && !useCounter && this._dotCount !== pages) {
        this._dotCount = pages;
        this.dotRow = makeDots(this.dots, pages, (i) => this.track.scrollTo({ left: i * this.step, behavior: reduceMotion.matches ? 'auto' : 'smooth' }), this.dataset.slideLabel);
      }
      if (useCounter) { this._dotCount = 0; this.dotRow = null; }
      const nav = this.querySelector('.ts-carousel__nav');
      if (nav) nav.hidden = pages <= 1;
      this.onScroll();
    }
    ensureArrows() {
      const nav = this.querySelector('.ts-carousel__nav');
      if (!nav || nav.querySelector('[data-ts-auto]')) return;
      if (this._ownPrev === undefined) { this._ownPrev = this.prev; this._ownNext = this.next; }
      const mk = (dir, label, d) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'ts-carousel__arrow'; b.dataset.tsAuto = ''; b.setAttribute(dir === -1 ? 'data-ts-prev' : 'data-ts-next', ''); b.setAttribute('aria-label', label); b.innerHTML = `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`; b.addEventListener('click', () => this.go(dir)); return b; };
      this.prev = mk(-1, 'Previous', 'M10 3 5 8l5 5'); this.next = mk(1, 'Next', 'M6 3l5 5-5 5');
      nav.prepend(this.prev); nav.append(this.next);
    }
    go(dir) {
      this.track.scrollBy({ left: dir * this.step, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
    onScroll() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      if (this.prev) this.prev.disabled = x <= 2;
      if (this.next) this.next.disabled = x >= max - 2;
      const idx = x >= max - 2 ? this.pages - 1 : Math.round(x / this.step);
      if (this.dotRow) this.dotRow.setActive(Math.max(0, Math.min(idx, this._dotCount - 1)));
      // counter counts cards ("1 / 12"), not scroll pages; at the end it shows the last card
      const total = this.slides.length;
      if (this.counter) this.counter.textContent = `${x >= max - 2 ? total : Math.min(idx + 1, total)} / ${total}`;
    }
  }
  if (!customElements.get('ts-carousel')) customElements.define('ts-carousel', TsCarousel);

  /* ---------- Sticky bottom bars ----------
     Visible only once the trigger (hero / main add-to-cart) has scrolled
     above the viewport, and hidden while the newsletter or footer is on
     screen so legal links and the country selector are never covered. */
  const initStickyBars = () => {
    const bars = [...document.querySelectorAll('[data-ts-sticky-bar]')].filter((bar) => !bar.dataset.tsReady);
    if (!bars.length || !('IntersectionObserver' in window)) return;
    document.body.classList.add('ts-has-sticky');
    const enders = document.querySelectorAll('.ts-footer, .ts-newsletter');
    let endVisible = false;
    const states = new Map();
    const apply = () => {
      states.forEach((past, bar) => {
        const show = past && !endVisible;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        bar.querySelectorAll('a, button').forEach((el) => (show ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1')));
      });
    };
    const seen = new Set();
    const endIo = new IntersectionObserver((entries) => {
      entries.forEach((en) => (en.isIntersecting ? seen.add(en.target) : seen.delete(en.target)));
      endVisible = seen.size > 0;
      apply();
    });
    enders.forEach((el) => endIo.observe(el));
    bars.forEach((bar) => {
      bar.dataset.tsReady = '1';
      const target = document.querySelector(bar.getAttribute('data-ts-sticky-bar'));
      if (!target) return;
      states.set(bar, false);
      new IntersectionObserver(([en]) => {
        states.set(bar, !en.isIntersecting && en.boundingClientRect.top < 0);
        apply();
      }).observe(target);
      // IntersectionObserver doesn't fire when a jump (anchor, scroll restore) skips over the
      // viewport, so a passive scroll check keeps the state right.
      let raf = 0;
      window.addEventListener('scroll', () => {
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          const past = target.getBoundingClientRect().bottom < 0;
          if (past !== states.get(bar)) { states.set(bar, past); apply(); }
        });
      }, { passive: true });
    });
  };
  /* ---------- Announcement rotation ---------- */
  class TsAnnouncement extends HTMLElement {
    connectedCallback() {
      this.msgs = [...this.querySelectorAll('.ts-announcement__msg')];
      if (this.msgs.length < 2 || reduceMotion.matches) return;
      this.i = 0;
      const speed = Number(this.dataset.speed || 5) * 1000;
      const tick = () => {
        if (this.paused) return;
        this.msgs[this.i].setAttribute('aria-hidden', 'true');
        this.i = (this.i + 1) % this.msgs.length;
        this.msgs[this.i].setAttribute('aria-hidden', 'false');
      };
      this.timer = setInterval(tick, speed);
      this.addEventListener('mouseenter', () => (this.paused = true));
      this.addEventListener('mouseleave', () => (this.paused = false));
      this.addEventListener('focusin', () => (this.paused = true));
      this.addEventListener('focusout', () => (this.paused = false));
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  if (!customElements.get('ts-announcement')) customElements.define('ts-announcement', TsAnnouncement);

  /* ---------- Testimonial "Read more" dialogs ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ts-dialog]');
    if (btn) {
      const dlg = document.getElementById(btn.getAttribute('data-ts-dialog'));
      if (dlg && typeof dlg.showModal === 'function') {
        e.preventDefault();
        // page behind is locked while open; focus goes back to the trigger on close (Esc, X or backdrop)
        dlg._tsTrigger = btn;
        if (!dlg._tsBound) {
          dlg._tsBound = true;
          dlg.addEventListener('close', () => {
            document.body.classList.remove('overflow-hidden');
            dlg._tsTrigger?.focus({ preventScroll: true });
          });
        }
        document.body.classList.add('overflow-hidden');
        dlg.showModal();
        dlg.scrollTop = 0;
        dlg.querySelector('[data-ts-dialog-close]')?.focus();
      }
      return;
    }
    const close = e.target.closest('[data-ts-dialog-close]');
    if (close) close.closest('dialog')?.close();
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('ts-modal')) e.target.close();
  });

  /* ---------- Search panel (replaces Dawn's modal) ---------- */
  class TsSearch extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.input = this.querySelector('input[type="search"]');
      this.clear = this.querySelector('[data-ts-search-clear]');
      this.results = this.querySelector('[data-ts-search-results]');
      this.openers = () => document.querySelectorAll('[data-ts-search-open]');
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-ts-search-open]');
        if (opener) { e.preventDefault(); this.isOpen ? this.close() : this.open(opener); return; }
        if (this.isOpen && !this.contains(e.target)) this.close();
      });
      this.querySelector('[data-ts-search-close]').addEventListener('click', () => this.close());
      this.clear.addEventListener('click', () => { this.input.value = ''; this.clear.hidden = true; this.results.innerHTML = ''; this.input.focus(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      this.input.addEventListener('input', () => {
        this.clear.hidden = !this.input.value;
        clearTimeout(this._t);
        this._t = setTimeout(() => this.fetch(), 220);
      });
    }
    get isOpen() { return this.classList.contains('is-open'); }
    open(trigger) {
      this.trigger = trigger;
      this.classList.add('is-open');
      this.openers().forEach((b) => b.setAttribute('aria-expanded', 'true'));
      window.setTimeout(() => this.input.focus(), 30);
    }
    close() {
      this.classList.remove('is-open');
      this.openers().forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
    }
    async fetch() {
      const q = this.input.value.trim();
      if (!q) { this.results.innerHTML = ''; return; }
      try {
        const url = `${(window.routes && window.routes.predictive_search_url) || '/search/suggest'}?q=${encodeURIComponent(q)}&resources[type]=product,page,article&resources[limit]=6&resources[limit_scope]=each&section_id=ts-predictive-search`;
        const html = await (await fetch(url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('.shopify-section');
        if (this.input.value.trim() === q) this.results.innerHTML = inner ? inner.innerHTML : '';
      } catch (err) { /* keep the previous results */ }
    }
  }
  if (!customElements.get('ts-search')) customElements.define('ts-search', TsSearch);

  /* ---------- Country / currency popover (Shopify localization form) ---------- */
  class TsCountry extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.btn = this.querySelector('[data-ts-country-toggle]');
      this.pop = this.querySelector('[data-ts-country-pop]');
      this.search = this.querySelector('[data-ts-country-search]');
      this.form = this.querySelector('form');
      this.btn.addEventListener('click', () => (this.pop.hidden ? this.open() : this.close()));
      this.list = this.querySelector('.ts-country__list');
      this.search.addEventListener('input', () => {
        const q = this.search.value.trim().toLowerCase();
        this.querySelectorAll('.ts-country__opt').forEach((o) => { o.parentElement.hidden = q && !o.dataset.name.includes(q); });
      });
      this.list.addEventListener('click', (e) => {
        const o = e.target.closest('.ts-country__opt');
        if (!o) return;
        this.querySelector('[data-ts-country-input]').value = o.dataset.value;
        this.form.submit();
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.pop.hidden) { this.close(); this.btn.focus(); } });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
    }
    // The ~240 countries ship as compact JSON and become buttons on first open (keeps every page ~100KB lighter).
    build() {
      if (this._built) return;
      this._built = true;
      const data = this.querySelector('[data-ts-countries]');
      if (!data) return;
      const { current, check, items } = JSON.parse(data.textContent);
      const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      this.list.innerHTML = items.map(([code, name, cur, sym]) => {
        const sel = code === current;
        return `<li><button type="button" class="ts-country__opt" role="option" aria-selected="${sel}" data-value="${code}" data-name="${esc(name.toLowerCase())}"><span>${esc(name)}</span><span class="ts-country__cur">${cur} ${esc(sym)}</span>${sel ? `<span class="ts-country__check">${check}</span>` : ''}</button></li>`;
      }).join('');
    }
    open() {
      this.build();
      this.pop.hidden = false;
      this.btn.setAttribute('aria-expanded', 'true');
      const cur = this.querySelector('.ts-country__opt[aria-selected="true"]');
      if (cur) cur.scrollIntoView({ block: 'center' });
      this.search.focus();
    }
    close() { this.pop.hidden = true; this.btn.setAttribute('aria-expanded', 'false'); }
  }
  if (!customElements.get('ts-country')) customElements.define('ts-country', TsCountry);

  /* ---------- Back to top (footer) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ts-back-to-top]');
    if (!btn) return;
    window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    const target = document.querySelector('a.ts-header__logo, .ts-header__logo a');
    if (target) target.focus({ preventScroll: true });
  });

  /* ---------- Boot ---------- */
  const boot = () => {
    initReveal();
    initHeader();
    initAccordions();
    initScrollers();
    initStickyBars();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target);
    initAccordions(e.target);
    initScrollers(e.target);
    initStickyBars();
    if (e.target.querySelector('[data-ts-header]')) initHeader();
  });
})();
